import React, { useState, useRef, useEffect } from 'react';
import { FaPaperPlane, FaSpinner } from 'react-icons/fa';
import { SMS_MAX_CHARS, SMS_RATE_AUD, smsSegments } from '../../utils/smsText';

// Reply box for a thread. A single SMS to one person is a low-stakes, high-frequency action, so it
// sends on Enter without a confirmation modal (unlike a broadcast, which is irreversible at scale).
// The cost of a long message is still shown up front rather than discovered on the bill.

const MAX_ROWS = 6;

const MessageComposer = ({ onSend, disabled, disabledReason, sending, recipientLabel }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  const trimmed = text.trim();
  const segments = smsSegments(trimmed);
  const overOneSegment = trimmed.length > SMS_MAX_CHARS;
  const canSend = !!trimmed && !disabled && !sending;

  // Grow with the message up to a ceiling, then scroll, so a long reply is readable while typing.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseInt(window.getComputedStyle(el).lineHeight, 10) || 20;
    el.style.height = `${Math.min(el.scrollHeight, lineHeight * MAX_ROWS)}px`;
  }, [text]);

  const submit = async () => {
    if (!canSend) return;
    setError(null);
    try {
      await onSend(trimmed);
      setText(''); // clear only once the send resolved, so a failure never loses what was typed
    } catch (e) {
      setError(e.message || 'Failed to send message.');
    }
  };

  // Enter sends, Shift+Enter adds a line: the convention operators already expect from a chat box.
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="border-t border-gray-200 bg-white p-3">
      {disabled && disabledReason && (
        <p className="mb-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {disabledReason}
        </p>
      )}
      {error && (
        <p className="mb-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-end gap-2">
        <label htmlFor="conversation-reply" className="sr-only">
          {recipientLabel ? `Message to ${recipientLabel}` : 'Message'}
        </label>
        <textarea
          id="conversation-reply"
          ref={textareaRef}
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || sending}
          placeholder={disabled ? 'Sending is unavailable for this conversation' : 'Type a message… (Enter to send, Shift+Enter for a new line)'}
          className="flex-1 resize-none text-sm border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan disabled:bg-gray-50 disabled:text-gray-400"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!canSend}
          className="shrink-0 h-10 w-10 flex items-center justify-center rounded-full text-white bg-jims-cyan hover:bg-jims-cyan-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-jims-cyan disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label={recipientLabel ? `Send SMS to ${recipientLabel}` : 'Send SMS'}
        >
          {sending ? <FaSpinner className="animate-spin" aria-hidden="true" /> : <FaPaperPlane aria-hidden="true" />}
        </button>
      </div>

      <div className="mt-1 flex items-center justify-between gap-3 text-xs">
        <span className="text-gray-400">
          {recipientLabel ? `To ${recipientLabel}` : ''}
        </span>
        <span className={overOneSegment ? 'text-amber-700 font-medium' : 'text-gray-400'}>
          {trimmed.length}/{SMS_MAX_CHARS} chars
          {segments > 1 && ` · ${segments} segments · ~$${(segments * SMS_RATE_AUD).toFixed(2)} AUD`}
        </span>
      </div>
    </div>
  );
};

export default MessageComposer;
